/**
 * supabaseLlmUsage.ts — Append-only Logging-Schicht für `llm_usage_log`.
 *
 * Diese Library schreibt für JEDEN LLM-Call genau einen Eintrag, unabhängig
 * vom Status (success / error / blocked_*). Doppelt geloggt wird zusätzlich
 * über die audit_log-Tabelle mit action='LLM_CALL' (siehe Insert unten).
 *
 * Sicherheits-Garantien (siehe Migration 0025):
 *   - Append-only via Trigger (kein UPDATE möglich).
 *   - called_by wird DB-seitig zu auth.uid() erzwungen.
 *   - SELECT für Admin (alle) + User (eigene).
 *   - DELETE nur Admin (für 10-Jahres-Cleanup).
 *
 * DSGVO-Schutz:
 *   - KEINE PII in dieser Tabelle — nur SHA-256-Hash + Metadata.
 *   - Token-Counts + duration_ms für Cost-Monitoring.
 *   - error_message darf KEINE Mandantendaten enthalten, nur API-Fehler.
 */

import { getSupabaseClient } from './supabase';
import type { LlmCallStatus, LlmProvider, LlmUsageLogRow } from './supabaseTypes';

const FIELD_KEY_RE = /^[a-zA-Z][a-zA-Z0-9_.]{1,99}$/;
const HASH_RE = /^[a-f0-9]{64}$/i; // SHA-256 hex

export interface LlmUsageEntry {
  /** Optional — manche Helper-Calls haben keinen Case-Kontext (Smoke-Tests) */
  readonly caseId: string | null;
  /** z.B. 'verdictLongParagraph', 'riskIndicators', 'dimensionTexts.identity' */
  readonly fieldKey: string;
  readonly provider: LlmProvider;
  /** Hex-kodierter SHA-256 des pseudonymisierten Input-Bodies. */
  readonly pseudonymizedHash: string;
  readonly promptTokens?: number;
  readonly completionTokens?: number;
  readonly totalTokens?: number;
  readonly durationMs?: number;
  readonly status: LlmCallStatus;
  /** Optional, nur bei Status != 'success'. KEINE PII. */
  readonly errorMessage?: string;
}

function validateEntry(entry: LlmUsageEntry): void {
  if (!FIELD_KEY_RE.test(entry.fieldKey)) {
    throw new Error(`logLlmUsage: fieldKey "${entry.fieldKey}" ungültig`);
  }
  if (!HASH_RE.test(entry.pseudonymizedHash)) {
    throw new Error('logLlmUsage: pseudonymizedHash muss 64-stelliger Hex-SHA-256 sein');
  }
}

/**
 * Schreibt einen Usage-Log-Eintrag. Wirft NICHT, falls die DB-Operation
 * scheitert — wir wollen, dass der eigentliche LLM-Flow nicht durch
 * Logging-Fehler blockiert wird. Stattdessen console.error.
 *
 * (Bewusste Wahl: append-only Logging ist „best effort", nicht „blocking".
 * Ein verloreners Log ist weniger schlimm als ein abgebrochener Berater-Flow.)
 */
export async function logLlmUsage(entry: LlmUsageEntry): Promise<void> {
  validateEntry(entry);
  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    console.error('logLlmUsage: auth.getUser failed', userErr.message);
    return;
  }
  const uid = userData.user?.id;
  if (typeof uid !== 'string') {
    console.error('logLlmUsage: kein eingeloggter User — Eintrag wird verworfen');
    return;
  }

  const insertPayload = {
    called_by: uid,
    case_id: entry.caseId,
    field_key: entry.fieldKey,
    provider: entry.provider,
    pseudonymized_hash: entry.pseudonymizedHash.toLowerCase(),
    prompt_tokens: entry.promptTokens ?? null,
    completion_tokens: entry.completionTokens ?? null,
    total_tokens: entry.totalTokens ?? null,
    duration_ms: entry.durationMs ?? null,
    status: entry.status,
    error_message: entry.errorMessage ?? null,
  };

  const { error } = await sb.from('llm_usage_log').insert(insertPayload as never);
  if (error) {
    console.error('logLlmUsage: insert failed —', error.code, error.message);
  }
}

/**
 * Liest alle Usage-Einträge für einen Case — Berater-Cockpit zeigt eine
 * Liste „X mal LLM aufgerufen, davon Y erfolgreich".
 */
export async function listLlmUsageForCase(caseId: string): Promise<LlmUsageLogRow[]> {
  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('llm_usage_log')
    .select('*')
    .eq('case_id', caseId)
    .order('called_at', { ascending: false });

  if (error) {
    throw new Error(`listLlmUsageForCase [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

export interface LlmUsageStats {
  readonly total: number;
  readonly byStatus: Record<LlmCallStatus, number>;
  readonly last24h: number;
}

const ZERO_STATS: Readonly<Record<LlmCallStatus, number>> = {
  success: 0,
  error: 0,
  blocked_flag: 0,
  blocked_pseudonymization: 0,
};

/**
 * Aggregierte Stats für das Admin-Dashboard. Effizienter wäre eine Postgres-
 * View; für den Pilot reicht der client-seitige Aggregat über RLS-gefilterte
 * Zeilen.
 *
 * Ohne Admin-Rolle sieht der User nur eigene Einträge — Stats spiegeln das.
 */
export async function listLlmUsageStats(): Promise<LlmUsageStats> {
  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('llm_usage_log')
    .select('*')
    .order('called_at', { ascending: false })
    .limit(1000);

  if (error) {
    throw new Error(`listLlmUsageStats [${error.code ?? '??'}]: ${error.message}`);
  }

  const byStatus: Record<LlmCallStatus, number> = { ...ZERO_STATS };
  let last24h = 0;
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  // Database-Type-Inferenz für SELECT-Resultate ist im hand-getypten
  // Schema unvollständig (siehe Kommentar in supabaseTypes.ts); wir casten
  // explizit auf die Row.
  const rows = (data ?? []) as ReadonlyArray<LlmUsageLogRow>;

  for (const row of rows) {
    byStatus[row.status] = (byStatus[row.status] ?? 0) + 1;
    if (new Date(row.called_at).getTime() >= cutoff) {
      last24h++;
    }
  }

  return {
    total: rows.length,
    byStatus,
    last24h,
  };
}
